/**
 * AdminNotificationsTab Component
 * Email notifications tab for admin panel
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Bell, Send, Clock, Loader2, CheckCircle } from 'lucide-react';

const AdminNotificationsTab = ({
  notifications,
  notificationsLoading,
  sendingNotifications,
  fetchNotifications,
  handleSendNotifications
}) => {
  return (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-slate-900">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#0047AB]" />
              Email Notifications
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={fetchNotifications}
                disabled={notificationsLoading}
              >
                {notificationsLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Refresh'
                )}
              </Button>
              <Button
                onClick={handleSendNotifications}
                disabled={sendingNotifications}
                className="bg-[#0047AB] hover:bg-[#003380] text-white"
                data-testid="send-notifications-btn"
              >
                {sendingNotifications ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Now
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-xl mb-6">
            <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Automatic Notifications</h4>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              Users will be automatically notified by email when their course access expires:
            </p>
            <ul className="text-sm text-blue-700 dark:text-blue-400 mt-2 space-y-1">
              <li>• <strong>30 days</strong> before expiry</li>
              <li>• <strong>14 days</strong> before expiry</li>
              <li>• <strong>7 days</strong> before expiry</li>
            </ul>
          </div>

          {!notifications.pending.length && !notifications.recent_sent.length && !notificationsLoading && (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Click "Refresh" to load pending notifications.
              </p>
              <Button onClick={fetchNotifications}>
                Load
              </Button>
            </div>
          )}

          {notificationsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
            </div>
          )}

          {!notificationsLoading && (notifications.pending.length > 0 || notifications.recent_sent.length > 0) && (
            <>
              {/* Pending Notifications */}
              <div className="mb-8">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Pending Notifications ({notifications.pending.length})
                </h4>
                {notifications.pending.length > 0 ? (
                  <ScrollArea className="h-[250px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Course</TableHead>
                          <TableHead>Expires</TableHead>
                          <TableHead className="text-center">Remaining</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notifications.pending.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.user_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500">{item.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.track}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {item.expires_at?.slice(0, 10)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={
                                item.days_remaining <= 7 ? 'bg-red-500' :
                                item.days_remaining <= 14 ? 'bg-amber-500' : 'bg-blue-500'
                              }>
                                {item.notification_label}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                ) : (
                  <p className="text-slate-500 text-sm py-4">No pending notifications</p>
                )}
              </div>

              {/* Recent Sent */}
              <div>
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Recently Sent ({notifications.recent_sent.length})
                </h4>
                {notifications.recent_sent.length > 0 ? (
                  <ScrollArea className="h-[200px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Course</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Sent</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notifications.recent_sent.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">{item.email}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{item.track}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">{item.notification_type}</TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {item.sent_at?.slice(0, 16).replace('T', ' ')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                ) : (
                  <p className="text-slate-500 text-sm py-4">No notifications sent yet</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNotificationsTab;
